import { JsonObject, JsonProperty } from "typescript-json-serializer";
import {FontConfiguration} from "./font-configuration";
import {Callout3DConfiguration} from "./callout-configuration";
import Color from "@arcgis/core/Color";

@JsonObject()
export class Symbol3DVerticalOffsetConfiguration {
    /**
     * The maximum vertical symbol offset in world units.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-support-Symbol3DVerticalOffset.html#maxWorldLength)
     */
    @JsonProperty()
    declare screenLength?: number | string;
    /**
     * The minimum vertical symbol offset in world units.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-support-Symbol3DVerticalOffset.html#minWorldLength)
     */
    @JsonProperty()
    declare minWorldLength?: number;
    /**
     * Vertical symbol offset in screen size.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-support-Symbol3DVerticalOffset.html#screenLength)
     */
    @JsonProperty()
    declare maxWorldLength?: number;

    constructor() {}
}

@JsonObject()
export class SymbolConfiguration {
    /**
     * The color of the symbol.
     *
     * @default "black"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-Symbol.html#color)
     */
    @JsonProperty()
    declare color: Color | number[] | string;

    /**
     * The symbol type.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-Symbol.html#type)
     */
    @JsonProperty()
    declare type:
    | "simple-marker"
    | "picture-marker"
    | "simple-line"
    | "simple-fill"
    | "picture-fill"
    | "text"
    | "shield-label-symbol"
    | "point-3d"
    | "line-3d"
    | "polygon-3d"
    | "web-style"
    | "mesh-3d"
    | "label-3d"
    | "cim";

    constructor() {}
}

@JsonObject()
export class LineSymbolConfiguration extends SymbolConfiguration {
    /**
     * The symbol type.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-LineSymbol.html#type)
     */
    @JsonProperty()
    declare type: "simple-line";

    /**
     * The width of the symbol in points.
     *
     * @default 0.75
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-LineSymbol.html#width)
     */
    @JsonProperty()
    declare width: number | string;

    constructor() {
        super();
    }
}

@JsonObject()
export class LineSymbolMarkerConfiguration {
    /**
     * The color of the marker.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-LineSymbolMarker.html#color)
     */
    @JsonProperty()
    declare color: Color;

    /**
     * The placement of the marker(s) on the line.
     *
     * @default "begin-end"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-LineSymbolMarker.html#placement)
     */
    @JsonProperty()
    declare placement: "begin" | "end" | "begin-end";

    /**
     * The marker style.
     *
     * @default "arrow"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-LineSymbolMarker.html#style)
     */
    @JsonProperty()
    declare style: "arrow" | "circle" | "square" | "diamond" | "cross" | "x";
    /**
     * The symbol type.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-LineSymbolMarker.html#type)
     */
    declare type: "line-marker";

    constructor() {}
}

@JsonObject()
export class SimpleLineSymbolConfiguration extends LineSymbolConfiguration {
    /**
     * Specifies the cap style.
     *
     * @default "round"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-SimpleLineSymbol.html#cap)
     */
    @JsonProperty()
    declare cap: "butt" | "round" | "square";

    /**
     * Specifies the join style.
     *
     * @default "round"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-SimpleLineSymbol.html#join)
     */
    @JsonProperty()
    declare join: "miter" | "round" | "bevel";

    /**
     * Specifies the color, style, and placement of a symbol marker on the line.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-SimpleLineSymbol.html#marker)
     */
    @JsonProperty()
    declare marker?: LineSymbolMarkerConfiguration;

    /**
     * Maximum allowed ratio of the width of a miter join to the line width.
     *
     * @default 2
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-SimpleLineSymbol.html#miterLimit)
     */
    @JsonProperty()
    declare miterLimit: number;

    /**
     * Specifies the line style.
     *
     * @default "solid"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-SimpleLineSymbol.html#style)
     */
    @JsonProperty()
    declare style:
    | "dash"
    | "dash-dot"
    | "dot"
    | "long-dash"
    | "long-dash-dot"
    | "long-dash-dot-dot"
    | "none"
    | "short-dash"
    | "short-dash-dot"
    | "short-dash-dot-dot"
    | "short-dot"
    | "solid";

    constructor() {
        super();
    }
}

@JsonObject()
export class FillSymbolConfiguration extends SymbolConfiguration {
    /**
     * The outline of the polygon.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-FillSymbol.html#outline)
     */
    @JsonProperty()
    declare outline: SimpleLineSymbolConfiguration;

    /**
     * The symbol type.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-FillSymbol.html#type)
     */
    @JsonProperty()
    declare type: "simple-fill" | "picture-fill";

    constructor() {
        super();
    }
}

@JsonObject()
export class MarkerSymbolConfiguration extends SymbolConfiguration {
    /**
     * The angle of the marker relative to the screen in degrees.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-MarkerSymbol.html#angle)
     */
    @JsonProperty()
    declare angle: number;

    /**
     * The symbol type.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-MarkerSymbol.html#type)
     */
    @JsonProperty()
    declare type: "simple-marker" | "picture-marker";

    /**
     * The offset on the x-axis in points.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-MarkerSymbol.html#xoffset)
     */
    @JsonProperty()
    declare xoffset: number;

    /**
     * The offset on the y-axis in points.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-MarkerSymbol.html#yoffset)
     */
    @JsonProperty()
    declare yoffset: number;

    constructor() {
        super();
    }
}

@JsonObject()
export class PictureMarkerSymbolConfiguration extends MarkerSymbolConfiguration {
    /**
     * The height of the image in points.
     *
     * @default 12
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-PictureMarkerSymbol.html#height)
     */
    @JsonProperty()
    declare height: number;

    /**
     * The symbol type.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-PictureMarkerSymbol.html#type)
     */
    @JsonProperty()
    declare readonly type: "picture-marker";

    /**
     * The URL to an image or SVG document.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-PictureMarkerSymbol.html#url)
     */
    @JsonProperty()
    declare url: string;

    /**
     * The width of the image in points.
     *
     * @default 12
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-PictureMarkerSymbol.html#width)
     */
    @JsonProperty()
    declare width: number;

    constructor() {
        super();
    }
}

@JsonObject()
export class SimpleMarkerSymbolConfiguration extends MarkerSymbolConfiguration {
    /**
     * The outline of the marker symbol.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-SimpleMarkerSymbol.html#outline)
     */
    @JsonProperty()
    declare outline: SimpleLineSymbolConfiguration;

    /**
     * The SVG path of the icon.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-SimpleMarkerSymbol.html#path)
     */
    @JsonProperty()
    declare path: string;

    /**
     * The size of the marker in points.
     *
     * @default 12
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-SimpleMarkerSymbol.html#size)
     */
    @JsonProperty()
    declare size: number;

    /**
     * The marker style.
     *
     * @default "circle"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-SimpleMarkerSymbol.html#style)
     */
    @JsonProperty()
    declare style: "circle" | "square" | "cross" | "x" | "diamond" | "triangle" | "path";

    /**
     * The symbol type.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-SimpleMarkerSymbol.html#type)
     */
    @JsonProperty()
    declare readonly type: "simple-marker";

    constructor() {
        super();
    }
}

@JsonObject()
export class TextSymbolConfiguration extends SymbolConfiguration {
    /**
     * The angle of the text.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-TextSymbol.html#angle)
     */
    @JsonProperty()
    declare angle?: number;
    /**
     * The background color of the label's bounding box.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-TextSymbol.html#backgroundColor)
     */
    @JsonProperty()
    declare backgroundColor?: Color | number[] | string;
    /**
     * The border color of the label's bounding box.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-TextSymbol.html#borderLineColor)
     */
    @JsonProperty()
    declare borderLineColor?: Color | number[] | string;
    /**
     * The border size or width of the label's bounding box.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-TextSymbol.html#borderLineSize)
     */
    @JsonProperty()
    declare borderLineSize?: number;
    /**
     * The [Font](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-Font.html) used to style the text.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-TextSymbol.html#font)
     */
    @JsonProperty()
    declare font?: FontConfiguration;
    /**
     * The color of the text symbol's halo.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-TextSymbol.html#haloColor)
     */
    @JsonProperty()
    declare haloColor?: Color | number[] | string;
    /**
     * The size in points of the text symbol's halo.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-TextSymbol.html#haloSize)
     */
    @JsonProperty()
    declare haloSize?: number | string;
    /**
     * Adjusts the horizontal alignment of the text in multi-lines.
     *
     * @default "center"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-TextSymbol.html#horizontalAlignment)
     */
    @JsonProperty()
    declare horizontalAlignment?: "left" | "right" | "center";
    /**
     * Determines whether to adjust the spacing between characters in the text string.
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-TextSymbol.html#kerning)
     */
    @JsonProperty()
    declare kerning?: boolean;
    /**
     * The height of the space between each line of text.
     *
     * @default 1.0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-TextSymbol.html#lineHeight)
     */
    @JsonProperty()
    declare lineHeight: number;
    /**
     * The maximum length in points for each line of text.
     *
     * @default 192
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-TextSymbol.html#lineWidth)
     */
    @JsonProperty()
    declare lineWidth: number;
    /**
     * Determines whether every character in the text string is rotated.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-TextSymbol.html#rotated)
     */
    @JsonProperty()
    declare rotated?: boolean;
    /**
     * The text string to display in the view.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-TextSymbol.html#text)
     */
    @JsonProperty()
    declare text?: string;

    @JsonProperty()
    declare readonly type: "text";

    /**
     * Adjusts the vertical alignment of the text.
     *
     * @default "baseline"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-TextSymbol.html#verticalAlignment)
     */
    @JsonProperty()
    declare verticalAlignment?: "baseline" | "top" | "middle" | "bottom";
    /**
     * The offset on the x-axis in points.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-TextSymbol.html#xoffset)
     */
    @JsonProperty()
    declare xoffset?: number | string;
    /**
     * The offset on the y-axis in points.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-TextSymbol.html#yoffset)
     */
    @JsonProperty()
    declare yoffset?: number | string;

    constructor() {
        super();
    }
}


@JsonObject()
export class Symbol3DStyleOriginConfiguration {
    /**
     * a well-known esri-provided style, such as `EsriThematicShapesStyle`.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-Symbol3D.html#styleOrigin)
     */
    @JsonProperty()
    declare styleName?: string;
    /**
     * url to a style definition.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-Symbol3D.html#styleOrigin)
     */
    @JsonProperty()
    declare styleUrl?: string;
    /**
     * name of the symbol in the style referenced by styleName or styleUrl.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-Symbol3D.html#styleOrigin)
     */
    @JsonProperty()
    declare name: string;

    constructor() {}
}

@JsonObject()
export class Symbol3DConfiguration extends SymbolConfiguration {
    /**
     * The origin of the style from which the symbol was originally referenced.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-Symbol3D.html#styleOrigin)
     */
    @JsonProperty()
    declare styleOrigin: Symbol3DStyleOriginConfiguration;
    /**
     * A Collection of [Symbol3DLayer](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-Symbol3DLayer.html) objects used to visualize the graphic or feature.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-Symbol3D.html#symbolLayers)
     */
    // @JsonProperty()
    // symbolLayers: Symbol3DLayerConfiguration[];

    constructor() {
        super();
    }
}

@JsonObject()
export class PolygonSymbol3DConfiguration extends Symbol3DConfiguration {
    /**
     * The symbol type.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-PolygonSymbol3D.html#type)
     */
    @JsonProperty()
    declare readonly type: "polygon-3d";

    constructor() {
        super();
    }
}

@JsonObject()
export class LabelSymbol3DConfiguration extends Symbol3DConfiguration {
    /**
     * Settings for adding a callout visualization to the symbol.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-LabelSymbol3D.html#callout)
     */
    @JsonProperty()
    declare callout?: Callout3DConfiguration;
    /**
     * A Collection of [Symbol3DLayer](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-Symbol3DLayer.html) objects used to visualize the graphic or feature.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-LabelSymbol3D.html#symbolLayers)
     */
    // @JsonProperty()
    //declare symbolLayers?: Collection<TextSymbol3DLayerConfiguration>;
    /**
     * The symbol type.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-LabelSymbol3D.html#type)
     */
    declare readonly type: "label-3d";
    /**
     * Shifts the symbol along the vertical world axis by a given height.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-LabelSymbol3D.html#verticalOffset)
     */
    @JsonProperty()
    declare verticalOffset?: Symbol3DVerticalOffsetConfiguration;

    constructor() {
        super();
    }
}

@JsonObject()
export class CIMSymbolConfiguration extends SymbolConfiguration {
    /**
     * The JSON payload of the [CIMSymbolReference](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-CIMSymbol.html#CIMSymbolReference).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-CIMSymbol.html#data)
     */
    @JsonProperty()
    declare data?: __esri.CIMSymbolReference;

    constructor() {
        super();
    }
}
